import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiCheck, FiFileText, FiUploadCloud, FiUser } from "react-icons/fi";
import LogicContext from "../../Context/LogicContext";
import { employeeService } from "../../services/employee.service";
import { ModalShell } from "./EmployeeDocumentsTab";

const STEPS = ["Personal Information", "Documents", "Waiting Content", "Waiting Content", "Waiting Content", "Waiting Content"];

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/png"];

// Each select field reads its options from a list in the employee endpoint
// response. A field with parentKey only shows options of the selected parent.
const SOURCES = {
  salutations: { id: "salutation_id", name: "salutation_name" },
  genders: { id: "gender_id", name: "gender_name" },
  nationalities: { id: "country_id", name: "country_name" },
  departments: { id: "department_id", name: "department_name" },
  subDepartments: { id: "sub_department_id", name: "sub_department_name", parentField: "department_id" },
  designations: { id: "designation_id", name: "designation_name", parentField: "department_id" },
  jobLocations: { id: "job_location_id", name: "job_location_name" },
  employeeTypes: { id: "employee_type_id", name: "employee_type_name" },
  employeeCategories: { id: "employee_category_id", name: "employee_category_name", parentField: "employee_type_id" },
  statuses: { id: "status_id", name: "status_name" },
};

const FIELDS = [
  { key: "salutationId", label: "Title", type: "select", required: true, source: "salutations" },
  { key: "firstName", label: "First Name", required: true, maxLength: 60 },
  { key: "middleName", label: "Middle Name (Optional)", maxLength: 60 },
  { key: "lastName", label: "Lastname", required: true, maxLength: 60 },
  { key: "dateOfBirth", label: "Date of Birth", type: "date", required: true },
  { key: "nationalityId", label: "Nationality", type: "select", required: true, source: "nationalities" },
  { key: "nationalId", label: "National ID / Passport No", maxLength: 40 },
  { key: "genderId", label: "Gender", type: "select", required: true, source: "genders" },
  { key: "companyEmail", label: "Company Email", type: "email", required: true, maxLength: 100 },
  { key: "companyMobile", label: "Company Mobile", type: "tel", required: true, maxLength: 20 },
  { key: "dateOfJoin", label: "Date of Joining", type: "date", required: true },
  { key: "department", label: "Department", type: "select", required: true, source: "departments" },
  { key: "subDepartmentId", label: "Sub Department", type: "select", required: true, source: "subDepartments", parentKey: "department", parentLabel: "Department" },
  { key: "designationId", label: "Designation", type: "select", required: true, source: "designations", parentKey: "department", parentLabel: "Department" },
  { key: "jobLocationId", label: "Job Location", type: "select", required: true, source: "jobLocations" },
  { key: "employeeType", label: "Employment Type", type: "select", required: true, source: "employeeTypes" },
  { key: "employeeCategoryId", label: "Employee Category", type: "select", required: true, source: "employeeCategories", parentKey: "employeeType", parentLabel: "Employment Type" },
  { key: "statusId", label: "Status", type: "select", required: true, source: "statuses", createOnly: true },
  { key: "contractStart", label: "Contract Start Date (Optional)", type: "date" },
  { key: "contractEnd", label: "Contract End Date (Optional)", type: "date" },
  { key: "probationPeriod", label: "Probation Period (Months)", type: "number", required: true },
  { key: "noticePeriod", label: "Notice Period (Months)", type: "number", required: true },
];

const DOCUMENT_SLOTS = [
  { key: "profilePicture", label: "Profile Picture", required: false },
  { key: "offerLetter", label: "Signed Offer Letter", required: false },
  { key: "certificate", label: "Academic Certificate", required: false },
  { key: "nationalId", label: "National Identity Card", required: false },
  { key: "others", label: "Others", required: false },
];

// Documents are uploaded to storage as soon as they are picked. Only the profile
// picture has a field to be saved in (photo_url), so the other URLs are not
// saved against the employee yet.
const UPLOAD_TYPES = {
  profilePicture: "employee-photo",
  offerLetter: "offer-letter",
  certificate: "academic-certificate",
  nationalId: "national-id",
  others: "others",
};

const EMPTY_FORM = Object.fromEntries(FIELDS.map((field) => [field.key, ""]));

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
}

function Stepper({ current }) {
  return (
    <ol className="grid grid-cols-6">
      {STEPS.map((label, index) => {
        const step = index + 1;
        const done = step < current;
        const active = step === current;
        return (
          <li key={index} className="relative flex flex-col items-center text-center">
            {index > 0 ? (
              <span
                className={`absolute right-1/2 top-3 h-px w-full ${step <= current ? "bg-blue-500" : "bg-gray-300"}`}
                aria-hidden="true"
              />
            ) : null}
            <span
              className={`relative z-10 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                done || active ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-500"
              } ${active ? "ring-4 ring-blue-100" : ""}`}
            >
              {done ? <FiCheck className="h-3.5 w-3.5" /> : step}
            </span>
            <span
              className={`mt-1.5 px-1 text-[10px] leading-tight sm:text-[11px] ${
                active || done ? "font-medium text-gray-900" : "text-gray-400"
              }`}
            >
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function FormField({ field, value, error, options, disabled, onChange }) {
  const label = `${field.label}${field.required ? "*" : ""}`;
  const base =
    "mt-1 h-10 w-full rounded-md bg-slate-100 px-3 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500";
  return (
    <div>
      <label className="block text-xs font-medium text-gray-800">{label}</label>
      {field.type === "select" ? (
        <select
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(field.key, event.target.value)}
          className={`${base} disabled:cursor-not-allowed disabled:opacity-60`}
        >
          <option value="">{disabled ? `Select ${field.parentLabel} first` : "Select..."}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={field.type ?? "text"}
          min={field.type === "number" ? 0 : undefined}
          value={value}
          onChange={(event) => onChange(field.key, event.target.value)}
          maxLength={field.maxLength}
          placeholder={field.type === "date" ? "Select Date" : "Input Text"}
          className={base}
        />
      )}
      {error ? <p className="mt-1 text-[11px] text-red-600">{error}</p> : null}
    </div>
  );
}

function UploadSlot({ slot, file, error, busy, onPick }) {
  const inputRef = useRef(null);
  return (
    <div className="border-b border-gray-100 py-5 last:border-0">
      <h3 className="text-sm font-semibold text-gray-900">{slot.label}</h3>
      <p className="mt-3 text-xs text-gray-800">Upload File/Document{slot.required ? "*" : ""}</p>
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-gray-600">
            <FiUploadCloud className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-900">{file ? file.name : "Upload your document"}</p>
            <p className="text-[11px] text-gray-500">JPG or PNG format · Max. 5MB</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="h-8 shrink-0 rounded-md border border-blue-500 px-4 text-xs font-medium text-blue-500 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? "Uploading..." : file ? "Change" : "Upload"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png"
          className="hidden"
          onChange={(event) => {
            const chosen = event.target.files?.[0];
            event.target.value = "";
            if (chosen) onPick(slot.key, chosen);
          }}
        />
      </div>
      {error ? <p className="mt-2 text-[11px] text-red-600">{error}</p> : null}
    </div>
  );
}

function PreviewRow({ label, value }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-3 py-1.5 text-xs @xl:grid-cols-[minmax(0,150px)_minmax(0,1fr)]">
      <dt className="text-gray-600">{label}</dt>
      <dd className="break-words font-semibold text-gray-900">{value || "-"}</dd>
    </div>
  );
}

function EmployeeCreatePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();
  // The parent route also has an :id (the company id), so useParams() returns an
  // id on /employees/new too. Only the /edit route is an edit.
  const isEdit = pathname.endsWith("/edit");
  const logicContext = useContext(LogicContext);
  const companyBase = `/${pathname.split("/")[1]}`;
  const moduleBase = `${companyBase}/Human-Resource`;

  const [stage, setStage] = useState("personal"); // "personal" | "documents" | "preview"
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [lists, setLists] = useState({});
  const [loadFailed, setLoadFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploadingKey, setUploadingKey] = useState("");
  const previewUrls = useRef({});

  const visibleFields = useMemo(
    () => FIELDS.filter((field) => !(isEdit && field.createOnly)),
    [isEdit]
  );

  useEffect(() => {
    document.title = isEdit ? "Edit Employee" : "Add New Employee";
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);

    const request = isEdit
      ? employeeService.getEmployee(id)
      : employeeService.getEmployees().then((result) => ({ filterOptions: result.filterOptions }));

    request
      .then((result) => {
        if (cancelled) return;
        if (!result) {
          setLoadFailed(true);
          return;
        }
        setLists(result.filterOptions);
        if (isEdit) {
          setPhotoUrl(result.photoUrl || "");
          setForm({
            salutationId: String(result.salutationId ?? ""),
            firstName: result.firstName === "-" ? "" : result.firstName,
            middleName: result.middleName,
            lastName: result.lastName === "-" ? "" : result.lastName,
            dateOfBirth: result.dateOfBirthInput,
            nationalityId: String(result.nationalityId ?? ""),
            nationalId: result.nationalId === "-" ? "" : result.nationalId,
            genderId: String(result.genderId ?? ""),
            companyEmail: result.email === "-" ? "" : result.email,
            companyMobile: result.companyMobile === "-" ? "" : result.companyMobile,
            dateOfJoin: result.dateOfJoinInput,
            department: String(result.departmentId ?? ""),
            subDepartmentId: String(result.subDepartmentId ?? ""),
            designationId: String(result.designationId ?? ""),
            jobLocationId: String(result.jobLocationId ?? ""),
            employeeType: String(result.employeeTypeId ?? ""),
            employeeCategoryId: String(result.employeeCategoryId ?? ""),
            statusId: "",
            contractStart: result.contractStartInput,
            contractEnd: result.contractEndInput,
            probationPeriod: result.probationPeriod === "-" ? "" : String(result.probationPeriod),
            noticePeriod: result.noticePeriod === "-" ? "" : String(result.noticePeriod),
          });
        }
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      Object.values(previewUrls.current).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [id, isEdit]);

  function optionsFor(field) {
    const source = SOURCES[field.source];
    return (lists[field.source] ?? [])
      .filter(
        (item) =>
          !field.parentKey || String(item[source.parentField]) === String(form[field.parentKey])
      )
      .map((item) => ({ value: String(item[source.id]), label: String(item[source.name]) }));
  }

  function labelOf(field) {
    const value = form[field.key];
    if (!value) return "";
    const source = SOURCES[field.source];
    const item = (lists[field.source] ?? []).find((entry) => String(entry[source.id]) === String(value));
    return item ? String(item[source.name]) : value;
  }

  function setField(key, value) {
    setForm((current) => {
      const next = { ...current, [key]: value };
      // Changing a parent clears the dependent selects that no longer match.
      FIELDS.forEach((field) => {
        if (field.parentKey === key) next[field.key] = "";
      });
      return next;
    });
    setErrors((current) => (current[key] ? { ...current, [key]: "" } : current));
  }

  function validatePersonal() {
    const next = {};
    visibleFields.forEach((field) => {
      const value = String(form[field.key] ?? "").trim();
      if (field.required && !value) next[field.key] = `${field.label.replace(" (Optional)", "")} is required.`;
    });
    ["companyEmail"].forEach((key) => {
      if (!next[key] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form[key].trim())) next[key] = "Enter a valid email address.";
    });
    if (!next.companyMobile && !/^\+?[\d\s-]{7,20}$/.test(form.companyMobile.trim())) {
      next.companyMobile = "Enter a valid phone number.";
    }
    if (!next.dateOfBirth && form.dateOfBirth > new Date().toISOString().slice(0, 10)) {
      next.dateOfBirth = "Date of birth cannot be in the future.";
    }
    if (form.contractStart && form.contractEnd && form.contractEnd < form.contractStart) {
      next.contractEnd = "End date cannot be earlier than start date.";
    }
    ["probationPeriod", "noticePeriod"].forEach((key) => {
      if (!next[key] && !/^\d{1,3}$/.test(form[key].trim())) next[key] = "Enter a whole number of months.";
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function validateDocuments() {
    const next = {};
    DOCUMENT_SLOTS.forEach((slot) => {
      if (slot.required && !files[slot.key]) next[slot.key] = `${slot.label} is required.`;
    });
    setFileErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handlePick(key, file) {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setFileErrors((current) => ({ ...current, [key]: "Only JPG or PNG files are allowed." }));
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileErrors((current) => ({ ...current, [key]: "File must be 5MB or smaller." }));
      return;
    }

    setUploadingKey(key);
    try {
      const url = await employeeService.uploadEmployeeFile(UPLOAD_TYPES[key], file);
      if (key === "profilePicture") setPhotoUrl(url);
    } catch (uploadError) {
      setFileErrors((current) => ({ ...current, [key]: uploadError?.message || "Upload failed." }));
      return;
    } finally {
      setUploadingKey("");
    }

    if (previewUrls.current[key]) URL.revokeObjectURL(previewUrls.current[key]);
    previewUrls.current[key] = URL.createObjectURL(file);
    setFiles((current) => ({ ...current, [key]: file }));
    setFileErrors((current) => ({ ...current, [key]: "" }));
  }

  function handleProceed() {
    if (stage === "personal") {
      if (!validatePersonal()) {
        logicContext.showToast("Please fix the highlighted fields", "error");
      } else {
        setStage(isEdit ? "preview" : "documents");
      }
    } else if (stage === "documents" && validateDocuments()) {
      setStage("preview");
    }
  }

  function handleBack() {
    if (stage === "documents") setStage("personal");
    else if (stage === "preview") setStage(isEdit ? "personal" : "documents");
    else navigate(isEdit ? `${moduleBase}/employees/${id}` : `${moduleBase}/dashboard`);
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      if (isEdit) {
        const result = await employeeService.updateEmployee(id, { ...form, photoUrl });
        logicContext.showToast(result.message, "success");
        navigate(`${moduleBase}/employees/${id}`);
      } else {
        const result = await employeeService.createEmployee({ ...form, photoUrl });
        logicContext.showToast(result.message, "success");
        navigate(result.refNo ? `${moduleBase}/employees/${result.refNo}` : `${moduleBase}/dashboard`);
      }
    } catch (submitError) {
      logicContext.showToast(submitError?.message || "Unable to save employee.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  const fieldLabel = (key) => labelOf(FIELDS.find((field) => field.key === key));
  const uploaded = DOCUMENT_SLOTS.filter((slot) => files[slot.key]);

  return (
    <div className="@container w-full min-w-0 pb-6">
      <nav className="flex flex-wrap items-center gap-1 text-xs text-gray-500" aria-label="Breadcrumb">
        <Link to={`${companyBase}/dashboard`} className="hover:text-blue-500">
          Home
        </Link>
        <span>/</span>
        <Link to={`${moduleBase}/dashboard`} className="hover:text-blue-500">
          HR
        </Link>
        <span>/</span>
        <Link to={`${moduleBase}/dashboard`} className="hover:text-blue-500">
          Employee Management
        </Link>
        <span>/</span>
        <span className="font-medium text-blue-500">{isEdit ? "Edit Employee" : "Add New Employee"}</span>
      </nav>

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={handleBack}
          aria-label="Back"
          className="rounded p-1 text-gray-700 hover:bg-gray-100"
        >
          <FiArrowLeft className="h-4 w-4" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">{stage === "preview" ? "Preview" : isEdit ? "Edit Employee" : "Add New Employee"}</h1>
      </div>

      {stage !== "preview" && !isEdit ? (
        <div className="mt-4">
          <Stepper current={stage === "personal" ? 1 : 2} />
        </div>
      ) : null}

      {stage === "personal" ? (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-5">
          <h2 className="text-base font-semibold text-gray-900">Employee Basic Information</h2>
          <p className="mt-1 text-xs text-gray-600">
            Ensure you enter correct information, some of the information provided will later be matched with your BVN details.
          </p>
          {loadFailed ? (
            <p className="mt-3 rounded bg-orange-50 px-3 py-2 text-xs text-orange-700">
              {isEdit ? "Employee details" : "The dropdown lists"} could not be loaded. Reload the page to try again.
            </p>
          ) : null}
          {loading ? <p className="mt-3 text-xs text-gray-500">Loading...</p> : null}
          {isEdit ? (
            <div className="mt-5 rounded-md border border-gray-100 px-4">
              {photoUrl ? (
                <img src={photoUrl} alt="Current profile" className="mt-4 h-16 w-16 rounded-full object-cover" />
              ) : null}
              <UploadSlot
                slot={DOCUMENT_SLOTS[0]}
                file={files.profilePicture}
                error={fileErrors.profilePicture}
                busy={uploadingKey === "profilePicture"}
                onPick={handlePick}
              />
            </div>
          ) : null}
          <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-4 @2xl:grid-cols-2">
            {visibleFields.map((field) => (
              <FormField
                key={field.key}
                field={field}
                value={form[field.key]}
                error={errors[field.key]}
                options={field.type === "select" ? optionsFor(field) : []}
                disabled={Boolean(field.parentKey) && !form[field.parentKey]}
                onChange={setField}
              />
            ))}
          </div>
        </div>
      ) : null}

      {stage === "documents" ? (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-5">
          <h2 className="text-base font-semibold text-gray-900">Documents and Attachments</h2>
          <p className="mt-1 text-xs text-gray-600">Attach a scanned copy or digital file to this employee's record.</p>
          <div className="mt-3">
            {DOCUMENT_SLOTS.map((slot) => (
              <UploadSlot key={slot.key} slot={slot} file={files[slot.key]} error={fileErrors[slot.key]} busy={uploadingKey === slot.key} onPick={handlePick} />
            ))}
          </div>
        </div>
      ) : null}

      {stage === "preview" ? (
        <>
          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-600 text-white">
                  <FiUser className="h-4 w-4" />
                </span>
                <h2 className="text-sm font-semibold text-gray-900">Basic Information</h2>
                <span className="rounded bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">KYC 100% Completed</span>
              </div>
              <button
                type="button"
                onClick={() => setStage("personal")}
                className="border-b border-indigo-600 text-xs font-medium text-indigo-600"
              >
                Edit
              </button>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-x-10 @3xl:grid-cols-2">
              <dl>
                {isEdit ? <PreviewRow label="Employee ID" value={id} /> : null}
                <PreviewRow label="Title" value={fieldLabel("salutationId")} />
                <PreviewRow label="First Name" value={form.firstName} />
                <PreviewRow label="Middle Name" value={form.middleName} />
                <PreviewRow label="Last Name" value={form.lastName} />
                <PreviewRow label="Gender" value={fieldLabel("genderId")} />
                <PreviewRow label="Date of Birth" value={formatDate(form.dateOfBirth)} />
                <PreviewRow label="Nationality" value={fieldLabel("nationalityId")} />
                <PreviewRow label="National ID / Passport No." value={form.nationalId} />
                <PreviewRow label="Company Email" value={form.companyEmail} />
                <PreviewRow label="Company Mobile" value={form.companyMobile} />
                <PreviewRow label="Date of Employment" value={formatDate(form.dateOfJoin)} />
              </dl>
              <dl>
                <PreviewRow label="Department" value={fieldLabel("department")} />
                <PreviewRow label="Sub Department" value={fieldLabel("subDepartmentId")} />
                <PreviewRow label="Designation" value={fieldLabel("designationId")} />
                <PreviewRow label="Job Location" value={fieldLabel("jobLocationId")} />
                <PreviewRow label="Employee Type" value={fieldLabel("employeeType")} />
                <PreviewRow label="Employee Category" value={fieldLabel("employeeCategoryId")} />
                <PreviewRow label="Contract Start Date" value={formatDate(form.contractStart)} />
                <PreviewRow label="Contract End Date" value={formatDate(form.contractEnd)} />
                <PreviewRow label="Probation Period (Month)" value={form.probationPeriod} />
                <PreviewRow label="Notice Period (Month)" value={form.noticePeriod} />
                {isEdit ? null : <PreviewRow label="Status" value={fieldLabel("statusId")} />}
              </dl>
            </div>
          </div>

          {isEdit ? null : (
          <div className="mt-4 rounded-lg border border-gray-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-600 text-white">
                  <FiFileText className="h-4 w-4" />
                </span>
                <h2 className="text-sm font-semibold text-gray-900">Document Attachment Uploads</h2>
              </div>
              <button
                type="button"
                onClick={() => setStage("documents")}
                className="border-b border-indigo-600 text-xs font-medium text-indigo-600"
              >
                Edit
              </button>
            </div>
            <ul className="mt-4 space-y-3">
              {uploaded.map((slot) => (
                <li key={slot.key} className="flex items-center justify-between gap-3 text-xs">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="truncate text-gray-800">{slot.label}</span>
                    <span className="shrink-0 rounded border border-green-200 bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">
                      Verified
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPreviewDoc({ label: slot.label, name: files[slot.key].name, url: previewUrls.current[slot.key] })}
                    className="shrink-0 rounded bg-slate-100 px-6 py-1.5 text-[11px] font-medium text-indigo-600 hover:bg-slate-200"
                  >
                    Preview
                  </button>
                </li>
              ))}
            </ul>
          </div>
          )}
        </>
      ) : null}

      {previewDoc ? (
        <ModalShell open onClose={() => setPreviewDoc(null)}>
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-900">{previewDoc.label}</h2>
            <p className="mt-1 text-[11px] text-gray-500">{previewDoc.name}</p>
            <div className="mt-5 flex h-[260px] w-full items-center justify-center overflow-hidden rounded bg-slate-100">
              <img src={previewDoc.url} alt={previewDoc.name} className="h-full w-full object-contain" />
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setPreviewDoc(null)}
                className="h-9 rounded-md bg-blue-600 px-5 text-xs font-medium text-white hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </ModalShell>
      ) : null}

      <div className="mt-6 flex items-center justify-end gap-3">
        {stage !== "preview" ? (
          <>
            <button
              type="button"
              onClick={handleBack}
              disabled={stage === "personal"}
              className="h-10 w-full max-w-[220px] rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-white"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleProceed}
              className="h-10 w-full max-w-[220px] rounded-md bg-blue-600 text-sm font-medium text-white hover:bg-blue-700"
            >
              Proceed
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={handleBack}
              disabled={submitting}
              className="h-10 w-full max-w-[220px] rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="h-10 w-full max-w-[220px] rounded-md bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "Saving..." : isEdit ? "Save Changes" : "Create New Employee"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default EmployeeCreatePage;
